import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { Logger } from '@montugroup/utilities';

const logger = new Logger('FFLoadingContext');

interface FFLoadingContextType {
  loading: boolean;
}

/**
 * React context for tracking LaunchDarkly initialization state.
 *
 * **Important**: This tracks when LaunchDarkly has successfully received
 * flag values from the server using the 'change' event, which is more
 * reliable than comparing values against bootstrap defaults.
 *
 */
export const FFLoadingContext = createContext<FFLoadingContextType>({
  loading: true,
});

/**
 * Provider component that tracks LaunchDarkly's readiness state.
 *
 * **Loading State Timeline:**
 * 1. **T+0ms**: Component mounts, loading=true, bootstrap flags available via useFlags()
 * 2. **T+100-2000ms**: LaunchDarkly connects to servers, fetches actual flag values
 * 3. **T+change**: 'change' event fires when server values received, loading=false
 *
 * **Detection Strategy:**
 * - Uses LaunchDarkly's 'change' event which fires when flag values are updated from the server
 * - Falls back to timeout if initialization fails or takes too long
 * - Handles errors gracefully by setting loading=false on any error
 *
 * @param children - React components that need feature flag loading state
 */
export const FFLoadingProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true);
  const ldClient = useLDClient();

  // Handle client initialization using the 'change' event
  useEffect(() => {
    if (!ldClient) {
      return;
    }

    // Fallback timeout in case initialization fails or takes too long
    const initializationTimeout = setTimeout(() => {
      if (loading) {
        logger.warn('LaunchDarkly initialization timeout - proceeding with default flag values');
        setLoading(false);
      }
    }, 3000);

    // The 'change' event fires when flag values are updated from the server
    // This indicates the client has connected and received actual flag values
    const handleChange = () => {
      logger.info('LaunchDarkly flags updated from server');
      clearTimeout(initializationTimeout);
      setLoading(false);
    };

    const handleError = (error: Error) => {
      logger.error('LaunchDarkly error', error);
      clearTimeout(initializationTimeout);
      setLoading(false);
    };

    ldClient.on('change', handleChange);
    ldClient.on('error', handleError);

    // Cleanup listeners and timeout on unmount
    return () => {
      clearTimeout(initializationTimeout);
      ldClient.off('change', handleChange);
      ldClient.off('error', handleError);
    };
  }, [ldClient, loading]);

  return <FFLoadingContext.Provider value={{ loading }}>{children}</FFLoadingContext.Provider>;
};

/**
 * Hook to access LaunchDarkly loading state.
 *
 * @returns Object with loading boolean
 * @throws {Error} If used outside of FFLoadingProvider
 */
export const useFFLoadingState = () => {
  const context = useContext(FFLoadingContext);
  if (context === undefined) {
    throw new Error('useFFLoadingState must be used within FFLoadingProvider');
  }
  return context;
};
